import React, { useEffect, useRef, useState } from "react";
import AppShell from "../components/AppShell";
import { api, humanBytes } from "../lib/api";
import { Search, Sparkles, Loader2, Image as ImageIcon, Video as VideoIcon, FileType2, FileText, FileQuestion } from "lucide-react";
import { Input } from "../components/ui/input";
import { Link, useSearchParams } from "react-router-dom";
import FileDetailDialog from "../components/FileDetailDialog";

const KIND_ICONS = {
    image: ImageIcon,
    video: VideoIcon,
    pdf: FileType2,
    text: FileText,
    other: FileQuestion,
};

export default function SearchPage() {
    const [params, setParams] = useSearchParams();
    const initial = params.get("q") || "";
    const [q, setQ] = useState(initial);
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [touched, setTouched] = useState(!!initial);
    const [selectedId, setSelectedId] = useState(null);
    const debounceRef = useRef(null);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            if (!q.trim() && !touched) return;
            setLoading(true);
            setTouched(true);
            try {
                const { data } = await api.get("/search", { params: { q, limit: 200 } });
                setResults(data.results || []);
                setParams(q ? { q } : {});
            } finally {
                setLoading(false);
            }
        }, 300);
        return () => debounceRef.current && clearTimeout(debounceRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q]);

    return (
        <AppShell>
            <div className="px-4 py-6 md:px-10 md:py-10 max-w-[1500px]" data-testid="search-page">
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">03 — Zoeken</div>
                <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-black leading-none mb-3">
                    Zoek alles, met taal.
                </h1>
                <p className="text-sm text-zinc-500 mb-6 md:mb-8 max-w-xl">
                    Zoek doorheen al je bestanden op AI-beschrijvingen, tags en bestandsnamen.
                </p>

                <div className="relative mb-6 md:mb-8">
                    <Search className="w-5 h-5 absolute left-3 md:left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        autoFocus
                        placeholder="Zoek bestanden…"
                        className="w-full bg-white border border-zinc-300 rounded-md pl-10 md:pl-12 pr-3 md:pr-4 py-3 md:py-4 text-base md:text-lg shadow-sm h-auto"
                        data-testid="global-search-input"
                    />
                </div>

                {loading && (
                    <div className="text-zinc-500 text-sm flex items-center gap-2 mb-4">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Zoeken…
                    </div>
                )}

                {!loading && touched && (
                    <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-4 font-mono">
                        {results.length} resultaat{results.length === 1 ? "" : "en"}
                    </div>
                )}

                {results.length > 0 && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
                        {results.map((r) => (
                            <SearchCard key={r.id} item={r} onClick={() => setSelectedId(r.id)} />
                        ))}
                    </div>
                )}

                {!loading && touched && results.length === 0 && (
                    <div className="border border-dashed border-zinc-300 rounded-md py-16 text-center bg-white">
                        <Search className="w-10 h-10 text-zinc-300 mx-auto mb-4" />
                        <div className="font-display text-xl tracking-tight mb-2">Geen resultaten</div>
                        <p className="text-sm text-zinc-500">
                            Probeer andere woorden of upload eerst bestanden in een project.
                        </p>
                    </div>
                )}
            </div>

            {selectedId && (
                <FileDetailDialog
                    fileId={selectedId}
                    onClose={() => setSelectedId(null)}
                    onDeleted={() => setResults((rs) => rs.filter((r) => r.id !== selectedId))}
                />
            )}
        </AppShell>
    );
}

function SearchCard({ item, onClick }) {
    const Icon = KIND_ICONS[item.kind] || FileQuestion;
    return (
        <button
            type="button"
            onClick={onClick}
            className="group relative bg-white border border-zinc-200 rounded-md overflow-hidden hover:border-[#0044FF] hover:shadow-md transition-all text-left"
            data-testid={`search-result-${item.id}`}
        >
            <div className="aspect-square bg-zinc-50 relative overflow-hidden flex items-center justify-center">
                {item.thumb_url ? (
                    <img
                        src={item.thumb_url}
                        alt={item.original_name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                    />
                ) : (
                    <div className="flex flex-col items-center gap-2 text-zinc-400">
                        <Icon className="w-12 h-12" />
                        <span className="text-[10px] font-mono uppercase">{item.kind}</span>
                    </div>
                )}
            </div>
            <div className="p-3">
                <div className="text-sm font-medium truncate">{item.original_name}</div>
                <div className="text-xs text-zinc-500 mt-1 truncate">
                    {item.customer_name} · {item.project_name}
                </div>
                {item.ai_description && (
                    <p className="text-xs text-zinc-600 mt-2 line-clamp-2 font-mono leading-snug">
                        {item.ai_description}
                    </p>
                )}
                {item.ai_tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                        {item.ai_tags.slice(0, 4).map((t) => (
                            <span key={t} className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-zinc-100 border border-zinc-200 text-zinc-700">
                                {t}
                            </span>
                        ))}
                    </div>
                )}
            </div>
            <div className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase text-white bg-black/70 px-1.5 py-0.5 rounded-sm">
                <Sparkles className="w-3 h-3" />
                AI
            </div>
        </button>
    );
}
